import React, { useState } from 'react';
import { Form, Tabs, Tooltip } from 'antd';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { ButtonItem } from './Items';
import { useMostrarHotkeys } from './HotKeyContext';

const { TabPane } = Tabs;

// Componente hoja que consume el contexto de hotkeys, para que TabbedDynamicTable
// (y por lo tanto la tabla de filas completa) no se re-renderice al presionar/soltar Shift.
const BadgeHotkeysTabs = () => {
  const mostrarHotkeys = useMostrarHotkeys();
  if (!mostrarHotkeys) return null;
  return <div className="tabs-hotkey-badge">Shift + ← / →</div>;
};

export const tabStyles = `
  .custom-tabs .ant-tabs-ink-bar {
    display: none !important;
  }

  .custom-tabs .ant-tabs-tab {
    padding: 4px 16px !important;
    margin: 0px 2px !important;
    border-radius: 8px 8px 0 0 !important;
    background: rgba(0, 191, 214, 0.15) !important;
    border: none !important;
    transition: background 0.15s ease-out;
  }

  .custom-tabs .ant-tabs-tab span {
    color: #1a3a4a !important;
    transition: color 0.15s ease-out;
  }

  .custom-tabs .ant-tabs-tab:hover {
    background: linear-gradient(135deg, #00BFD6 0%, #28d3ea 100%) !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
  }
  .custom-tabs .ant-tabs-tab:hover span {
    color: #ffffff !important;
  }

  .custom-tabs .ant-tabs-tab-active {
    background: linear-gradient(135deg, #00BFD6 0%, #28d3ea 100%) !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
  }

  .custom-tabs .ant-tabs-tab-active span {
    color: #ffffff !important;
  }

  .custom-tabs .ant-tabs-nav {
    margin: 0 !important;
  }

  .custom-tabs .ant-tabs-nav::before {
    border-bottom: 3px solid #00BFD6 !important;
  }

  .custom-tabs {
    margin-bottom: 10px;
  }

  .custom-tabs .ant-tabs-content-holder {
    background: linear-gradient(180deg, #ffffff 0%, #f5f9fa 100%);
    border: 1px solid #00BFD6;
    border-top: none;
    border-radius: 0 0 12px 12px;
    padding: 24px;
    box-shadow: 0 4px 12px rgba(0, 191, 214, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
  }

  .custom-tabs.no-content-border .ant-tabs-content-holder {
    background: transparent !important;
    border: none !important;
    border-top: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    margin-top: 20px !important;
  }

  .tabs-hotkey-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    background: rgba(0, 191, 214, 0.12);
    border: 1px solid #00BFD6;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    color: #00BFD6;
    margin-right: 16px;
  }

  .table-12px {
    font-size: 14px;
  }

  .table-12px th,
  .table-12px td {
    font-size: 14px;
  }

  .table-12px .ant-input,
  .table-12px .ant-picker,
  .table-12px .ant-select-selector {
    font-size: 14px !important;
  }

  .table-12px input::placeholder {
    font-size: 14px;
  }

  .table-12px .ant-input-number-input {
    font-size: 14px !important;
  }

  .table-12px td:first-child {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .table-12px td:first-child .ant-select {
    max-width: 100%;
  }

  .table-12px td:first-child .ant-select-selector {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/**
 * Cuerpo de la tabla cuando la pestaña NO agrupa: el `fields.map` de siempre.
 */
const CuerpoPlano = ({ tab, fields, remove, move, formInstance }) => (
  <tbody>
    {fields.length === 0 ? (
      <tr></tr>
    ) : (
      fields.map(({ key, name, fieldKey, ...restField }, arrayKey) =>
        tab.renderRow
          ? tab.renderRow({ key, name, fieldKey, restField, arrayKey, remove, move, formInstance })
          : null
      )
    )}
  </tbody>
);

/**
 * Reparte los `fields` de un Form.List en grupos, conservando de cada uno su
 * `arrayKey` REAL.
 *
 * La posición de cada grupo la fija su PRIMERA aparición, así que un grupo nunca
 * salta hacia arriba cuando se le agrega una fila al final de la lista.
 *
 * Se exporta para poder verificarla con node: es la pieza donde un error se
 * traduce en filas duplicadas o perdidas en pantalla.
 */
export function agruparFields(fields, obtenerGrupo) {
  const grupos = [];
  const porId = new Map();
  (fields || []).forEach((field, arrayKey) => {
    const grupo = obtenerGrupo(arrayKey) || { id: 'sin-grupo', nombre: '' };
    let acumulado = porId.get(grupo.id);
    if (!acumulado) {
      acumulado = { grupo, entradas: [] };
      porId.set(grupo.id, acumulado);
      grupos.push(acumulado);
    }
    acumulado.entradas.push({ field, arrayKey });
  });
  return grupos;
}

/**
 * Cuerpo agrupado: por grupo, un `<tbody>` con su encabezado y otro con sus filas.
 *
 * Dos invariantes que NO se pueden romper:
 *
 *  1. `renderRow` recibe el `arrayKey` / `name` / `key` ORIGINALES de `fields`.
 *     Agrupar sólo cambia el ORDEN en que se emiten las filas, nunca su
 *     identidad: de eso dependen `remove(name)`, las rutas de los `FormItem` y
 *     el `valores[arrayKey]` que lee cada fila.
 *
 *  2. Las filas de un grupo colapsado se ESCONDEN con `display: none`, no se
 *     desmontan. Los renglones traen `FormItem ... required` y antd sólo valida
 *     los campos montados: desmontarlos dejaría pasar una partida incompleta
 *     escondida. Varios `<tbody>` en una misma tabla son HTML válido.
 */
const CuerpoAgrupado = ({ tab, fields, remove, move, formInstance, colapsados, alternar }) => {
  const { obtenerGrupo, renderEncabezado } = tab.agrupacion;
  const grupos = agruparFields(fields, obtenerGrupo);
  const totalColumnas = tab.columns ? tab.columns.length : 1;

  return grupos.map(({ grupo, entradas }) => {
    const colapsado = colapsados.has(grupo.id);
    return (
      <React.Fragment key={grupo.id}>
        <tbody>
          {renderEncabezado({
            grupo,
            arrayKeys: entradas.map((e) => e.arrayKey),
            colapsado,
            alternar: () => alternar(grupo.id),
            totalColumnas,
          })}
        </tbody>
        <tbody style={colapsado ? { display: 'none' } : undefined}>
          {entradas.map(({ field, arrayKey }) => {
            const { key, name, fieldKey, ...restField } = field;
            return tab.renderRow
              ? tab.renderRow({ key, name, fieldKey, restField, arrayKey, remove, move, formInstance })
              : null;
          })}
        </tbody>
      </React.Fragment>
    );
  });
};

const TabbedDynamicTable = ({
  tabs = [], // Cada elemento define una pestaña y su tabla
  defaultActiveKey,
  onTabChange,
  isMobile = false,
  formInstance,
  tabBarExtraContent,
  tableClassName = 'tabla-insumos-venta', // Clase CSS personalizada para la tabla
  hideTabs = false, // Prop para ocultar la barra de tabs
  showContentBorder = true, // Prop opcional para mostrar u ocultar el borde azul del contenido
}) => {
  const [activeKey, setActiveKey] = useState(defaultActiveKey || tabs[0]?.key); // Estado para la pestaña activa
  const [scrollEnabled, setScrollEnabled] = useState(false); // Estado para controlar si el scroll está habilitado (false por defecto)

  // Grupos colapsados por pestaña: { [tab.key]: Set(idGrupo) }. Vive aquí y no en
  // las pantallas para que ninguna tenga que administrarlo. Todas abren
  // expandidas: colapsar es algo que el usuario pide, no un estado inicial.
  const [colapsadosPorTab, setColapsadosPorTab] = useState({});

  const alternarGrupo = React.useCallback((tabKey, idGrupo) => {
    setColapsadosPorTab((previo) => {
      const actuales = new Set(previo[tabKey] || []);
      if (actuales.has(idGrupo)) actuales.delete(idGrupo);
      else actuales.add(idGrupo);
      return { ...previo, [tabKey]: actuales };
    });
  }, []);

  const SIN_COLAPSAR = React.useMemo(() => new Set(), []);

  // Un solo lugar decide si la pestaña va agrupada o plana, para que las dos
  // ramas de scroll (con y sin) no se desincronicen.
  const renderCuerpo = (tab, { fields, remove, move }) => {
    const agrupada = tab.agrupacion && tab.agrupacion.activa && fields.length > 0;
    if (!agrupada) {
      return <CuerpoPlano tab={tab} fields={fields} remove={remove} move={move} formInstance={formInstance} />;
    }
    return (
      <CuerpoAgrupado
        tab={tab}
        fields={fields}
        remove={remove}
        move={move}
        formInstance={formInstance}
        colapsados={colapsadosPorTab[tab.key] || SIN_COLAPSAR}
        alternar={(idGrupo) => alternarGrupo(tab.key, idGrupo)}
      />
    );
  };

  const handleTabChange = React.useCallback((key) => {
    setActiveKey(key);
    if (onTabChange) {
      onTabChange(key);
    }
  }, [onTabChange]);

  const toggleScroll = () => {
    setScrollEnabled(prev => !prev);
  };

  // Hotkey global para navegar entre tabs con Shift + flechas
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.shiftKey) return;

      const active = document.activeElement;
      if (
        active &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.tagName === 'SELECT' ||
          active.isContentEditable ||
          !!active.closest('.ant-select'))
      ) return;

      if (tabs.length === 0) return;
      const currentIndex = tabs.findIndex(tab => tab.key === activeKey);

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % tabs.length;
        handleTabChange(tabs[nextIndex].key);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        handleTabChange(tabs[prevIndex].key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeKey, tabs, handleTabChange]);

  return ( // Contenedor principal
    <div style={{ 
      maxWidth: '100%', 
      width: '100%', 
      margin: '0 auto', 
      position: 'relative'
    }}>
      <Tabs
        activeKey={activeKey}
        onChange={handleTabChange}
        tabBarStyle={hideTabs ? {
          display: 'none'
        } : {
          background: 'transparent',
          margin: 0,
          padding: '0 16px',
          minHeight: 'auto',
        }}
        tabBarExtraContent={
          <>
            <BadgeHotkeysTabs />
            {tabBarExtraContent}
          </>
        }
        className={showContentBorder ? 'custom-tabs' : 'custom-tabs no-content-border'}
      >
        {tabs.map((tab) => (
          <TabPane
            disabled={tab.disabled}
            key={tab.key}
            tab={<span style={{ color: '#1a3a4a', fontWeight: 600, fontSize: 14 }}>{tab.label}</span>}
            forceRender={tab.forceRender}
          >
            <div ref={tab.ref || null} style={{ padding: 0 }}>

              {tab.customContent ? (
                tab.customContent
              ) : tab.listName ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 8, gap: 4 }}>
                    {tab.renderHeader && tab.renderHeader({})}
                    <Tooltip title={scrollEnabled ? "Deshabilitar scroll" : "Habilitar scroll"}>
                      <ButtonItem
                        type="text"
                        size="small"
                        icon={scrollEnabled ? <UnlockOutlined /> : <LockOutlined />}
                        onClick={toggleScroll}
                        style={{
                          fontSize: '13px',
                          cursor: 'pointer',
                          color: scrollEnabled ? '#00BFD6' : '#8c8c8c',
                          border: '1px solid',
                          borderColor: scrollEnabled ? '#00BFD6' : '#d9d9d9',
                          borderRadius: '4px',
                          padding: '0px',
                          minWidth: '24px',
                          width: '24px',
                          height: '24px',
                          maxHeight: '24px',
                          lineHeight: '24px',
                          background: scrollEnabled ? 'rgba(0, 191, 214, 0.08)' : '#fff',
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  </div>
                  <Form.List name={tab.listName}>
                  {(fields, { add, remove, move }) => {
                  return (
                    <>
                      {!(tab.hideTableWhenEmpty !== false && fields.length === 0) && (
                        scrollEnabled ? (
                          <div
                            className={tableClassName}
                            style={{
                              overflowX: 'auto',
                              overflowY: 'auto',
                              maxHeight: '45rem',
                              height: '25rem',
                            }}
                          >
                            <table
                              className="table table-12px"
                              style={{
                                minWidth: isMobile ? '900px' : 'auto',
                                width: '100%',
                                backgroundColor: 'white',
                                tableLayout: tab.tableLayout || 'auto'
                              }}
                            >
                              {fields.length > 0 && tab.columns && (
                                <thead
                                  style={{
                                    position: 'sticky',
                                    top: 0,
                                    backgroundColor: 'white',
                                    zIndex: 1
                                  }}
                                >
                                  <tr>
                                    {tab.columns.map((col, index) => (
                                      <th
                                        key={index}
                                        className="th-border"
                                        style={{ width: col.width }}
                                      >
                                        {col.title}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                              )}

                              {renderCuerpo(tab, { fields, remove, move })}
                            </table>
                          </div>
                        ) : (
                          // SIN SCROLL - Tabla completa sin contenedor restrictivo
                          <div className={tableClassName} style={{ overflowX: 'auto', overflowY: 'visible', maxHeight: 'none', height: 'auto' }}>
                            <table
                              className="table table-12px"
                              style={{
                                width: '100%',
                                minWidth: tab.minWidth || '1200px',
                                backgroundColor: 'white',
                                tableLayout: tab.tableLayout || 'auto'
                              }}
                            >
                              {fields.length > 0 && tab.columns && (
                                <thead style={{ backgroundColor: 'white' }}>
                                  <tr>
                                    {tab.columns.map((col, index) => (
                                      <th
                                        key={index}
                                        className="th-border"
                                        style={{ width: col.width }}
                                      >
                                        {col.title}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                              )}

                              {renderCuerpo(tab, { fields, remove, move })}
                            </table>
                          </div>
                        )
                      )}

                      {tab.renderFooter && tab.renderFooter({ add, fields })}
                    </>
                  );
                  }}
                  </Form.List>
                </>
              ) : null}
            </div>
          </TabPane>
        ))}
      </Tabs>

<style>{tabStyles}</style>
    </div>
  );
};

export default TabbedDynamicTable;